/**
 * This page lists all jobs within a project.
 */
import {WHITE} from '@wandb/weave/common/css/globals.styles';
import React, {useState} from 'react';

import {Analytics} from '../../../services/analytics';
import {BannerBoldText} from '../../Banner';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {useFetchProjectJobsIncrementally} from '../hooks';
import {JobDrawer} from '../JobDrawer/JobDrawer';
import {CloseableBanner} from './CloseableBanner';
import {JobsHeader} from './JobsHeader';
import * as S from './JobsPage.styles';
import {JobsTable} from './JobsTable';
import {getJobInfo} from './jobUtil';
import {ZeroJobs} from './ZeroJobs';

interface JobsPageProps {
  entityName: string;
  projectName: string;
}

export const JobsPage: React.FC<JobsPageProps> = ({
  entityName,
  projectName,
}) => {
  const [jobToRun, setJobToRun] = useState<string | null>(null);

  const {
    data,
    loading,
    refetch: refetchJobs,
  } = useFetchProjectJobsIncrementally(entityName, projectName);

  if (loading) {
    return <Loader active name="jobs-page-loader" />;
  }
  const jobEdges: any[] =
    data?.project?.artifactType?.artifactCollections?.edges ?? [];
  const jobsCount = jobEdges.length;

  if (jobsCount === 0) {
    // Note: BlankPage component does not match new UI style.
    return (
      <S.Content style={{backgroundColor: WHITE, height: '100%'}}>
        <JobsHeader />
        <ZeroJobs />
      </S.Content>
    );
  }

  const onRun = (jobId: string) => {
    setJobToRun(jobId);
  };
  const closeDrawer = () => {
    setJobToRun(null);
    Analytics.track('jobs drawer closed', {
      entityName,
    });
  };

  const onJobDelete = () => {
    refetchJobs();
  };

  const jobs = getJobInfo(jobEdges);

  return (
    <S.Content>
      <JobsHeader />
      <CloseableBanner hintKey="jobs">
        <BannerBoldText>Run your first job!</BannerBoldText>
        Jobs are reusable run templates that allow you and your team to quickly
        execute runs at scale, locally or on extra GPUs. To run a job, select a
        job and click "Launch".
      </CloseableBanner>
      <JobsTable
        jobs={jobs}
        entityName={entityName}
        projectName={projectName}
        onRun={onRun}
        onJobDelete={onJobDelete}
      />
      <JobDrawer
        open={jobToRun != null}
        closeDrawer={closeDrawer}
        entityName={entityName}
        projectName={projectName}
        defaultJobId={jobToRun ?? undefined}
        defaultJobVersion="latest"
      />
    </S.Content>
  );
};
